import React, { useEffect, useState, useCallback } from 'react';
import { X, Gift, Sparkles, CheckCircle2 } from 'lucide-react';

import SafeHtml from './SafeHtml';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WelcomeBonusModal — fires once for fresh signups on their first login.
 *
 * Design rules:
 *   - Asks the backend (`/api/auth/welcome-bonus/status`) whether to render.
 *     Migrated/legacy users always get `show=false` because they never had
 *     `is_new_signup=true` set, so this hook is a no-op for them.
 *   - On dismiss we POST `/welcome-bonus/dismiss` so the modal never reappears.
 *   - The modal HTML body is admin-editable (see WelcomeBonusEditor). We
 *     interpolate {{min_deposit}}, {{bonus_amount}}, {{validity_days}},
 *     {{total_balance}} placeholders client-side.
 *   - We also call `onRecharge()` when the user clicks the call-to-action,
 *     letting the parent panel deep-link them to the Recharge page.
 */
export default function WelcomeBonusModal({ token, onRecharge }) {
  const [data, setData] = useState(null); // null = not loaded yet, false = should hide
  const [closing, setClosing] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/auth/welcome-bonus/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { setData(false); return; }
      const j = await r.json();
      if (j && j.show) setData(j); else setData(false);
    } catch (_) { setData(false); }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const dismiss = useCallback(async () => {
    setClosing(true);
    // Optimistically hide — the API call is fire-and-forget. Re-mounts of
    // the panel won't bring the modal back because the server flag has flipped.
    try {
      await fetch(`${API_URL}/api/auth/welcome-bonus/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (_) { /* noop */ }
    setData(false);
  }, [token]);

  const handleCta = useCallback(async () => {
    await dismiss();
    if (onRecharge) onRecharge();
  }, [dismiss, onRecharge]);

  if (!data) return null;

  const fmt = (n) => Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2);
  const total = (Number(data.min_deposit) || 0) + (Number(data.bonus_amount) || 0);
  const html = (data.modal_html || '')
    .replaceAll('{{min_deposit}}',   fmt(data.min_deposit))
    .replaceAll('{{bonus_amount}}',  fmt(data.bonus_amount))
    .replaceAll('{{validity_days}}', String(Number(data.validity_days) || 0))
    .replaceAll('{{total_balance}}', fmt(total));

  return (
    <div
      className={`fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm ${closing ? 'animate-out fade-out' : 'animate-in fade-in'}`}
      data-testid="welcome-bonus-modal"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-2xl max-h-[92vh] overflow-hidden bg-white rounded-2xl shadow-2xl animate-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero header */}
        <div className="relative bg-gradient-to-br from-violet-600 via-blue-600 to-cyan-500 text-white px-6 sm:px-10 py-8 sm:py-10 text-center overflow-hidden">
          {/* Decorative sparkle dots */}
          <span className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
          <span className="absolute -bottom-10 -right-6 w-40 h-40 rounded-full bg-cyan-300/20 blur-3xl" />
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition-colors"
            data-testid="welcome-bonus-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm mb-4 shadow-lg">
            <Gift className="w-10 h-10" />
            <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-amber-300 animate-pulse" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="welcome-bonus-title">
            {data.modal_title || 'Welcome bonus for your registration on our file service'}
          </h2>
          <p className="mt-2 text-sm sm:text-base text-white/90">
            A <span className="font-bold text-amber-300">€{fmt(data.bonus_amount)}</span> bonus is waiting for you · valid {Number(data.validity_days) || 0} days
          </p>
        </div>

        {/* Body — admin-editable HTML rendered with brand styling */}
        <div className="px-6 sm:px-10 py-6 sm:py-8 max-h-[50vh] overflow-y-auto">
          <SafeHtml as="div" className="text-slate-700 leading-relaxed [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-violet-700 [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:my-2 [&_strong]:text-slate-900 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_ul]:my-2 [&_li]:my-1.5 [&_.wb-features]:bg-cyan-50 [&_.wb-features]:p-3 [&_.wb-features]:rounded-lg [&_.wb-features]:border-l-4 [&_.wb-features]:border-cyan-500 [&_.wb-features]:list-none [&_.wb-features]:pl-3 [&_.wb-features_li]:before:content-['✓_'] [&_.wb-features_li]:before:text-emerald-500 [&_.wb-features_li]:before:font-bold"
            data-testid="welcome-bonus-body"
            /* Admin-editable HTML — sourced from a server-side admin-only setting. */ html={html} />
        </div>

        {/* Footer with CTA */}
        <div className="px-6 sm:px-10 py-4 bg-gradient-to-r from-slate-50 to-violet-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span>One-time offer. Bonus auto-applies on your first deposit.</span>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={dismiss}
              data-testid="welcome-bonus-later"
              className="flex-1 sm:flex-none px-5 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium text-sm transition-colors"
            >
              Maybe later
            </button>
            <button
              onClick={handleCta}
              data-testid="welcome-bonus-recharge"
              className="flex-1 sm:flex-none px-5 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-lg font-bold text-sm shadow-md hover:shadow-lg hover:scale-[1.02] transition-all"
            >
              Top up & claim →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
